// frontend/src/pages/admin/AdminShipping.js
import React, { useState, useEffect, useMemo } from 'react';
import { zonesApi } from '../../api/zones';
import { shippingApi } from '../../api/shipping';
import helpers from '../../utils/helpers';
import { FaMapMarkedAlt, FaSave, FaSpinner, FaChevronDown, FaChevronRight, FaEdit } from 'react-icons/fa';

function AdminShipping() {
  const [lgas, setLgas] = useState([]);
  const [configuredRates, setConfiguredRates] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Selection State
  const [selectedState, setSelectedState] = useState('');
  const [defaultPrice, setDefaultPrice] = useState('');
  const [lgaOverrides, setLgaOverrides] = useState({});
  const [saving, setSaving] = useState(false);
  const [expandedState, setExpandedState] = useState(null); // For viewing list

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [lgasData, ratesData] = await Promise.all([
        zonesApi.getAllLgas(),
        shippingApi.getAllRates()
      ]);
      setLgas(lgasData);
      
      // Convert rates array to map for easy lookup: { "Lagos": { default: 3000, lgas: {...} } }
      const ratesMap = {};
      ratesData.forEach(r => {
          ratesMap[r.state] = r;
      });
      setConfiguredRates(ratesMap);
      
    } catch (err) {
      console.error("Failed to load data", err);
    } finally {
      setLoading(false);
    }
  };

  // Group LGAs by State
  const groupedLgas = useMemo(() => {
    return lgas.reduce((acc, lga) => {
      if (!lga.state || !lga.lga) return acc;
      const stateKey = lga.state.trim();
      if (!acc[stateKey]) acc[stateKey] = [];
      acc[stateKey].push(lga.lga);
      return acc;
    }, {});
  }, [lgas]);

  const handleStateSelect = (e) => {
      const state = e.target.value;
      setSelectedState(state);
      
      // Load existing config if available
      if (configuredRates[state]) {
          setDefaultPrice(configuredRates[state].default_price);
          setLgaOverrides(configuredRates[state].lga_rates || {});
      } else {
          setDefaultPrice('');
          setLgaOverrides({});
      }
  };

  const handleOverrideChange = (lgaName, value) => {
      setLgaOverrides(prev => ({
          ...prev,
          [lgaName]: value
      }));
  };

  const handleSave = async (e) => {
      e.preventDefault();
      if (!selectedState || !defaultPrice) {
          alert("Please select a state and set a default price.");
          return;
      }

      setSaving(true);
      try {
          // Filter out empty overrides
          const cleanOverrides = {};
          Object.keys(lgaOverrides).forEach(key => {
              if (lgaOverrides[key] && lgaOverrides[key] !== '') {
                  cleanOverrides[key] = parseFloat(lgaOverrides[key]);
              }
          });

          const payload = {
              state: selectedState,
              default_price: parseFloat(defaultPrice),
              lga_rates: cleanOverrides
          };

          await shippingApi.updateRate(payload);
          
          // Update local state
          setConfiguredRates(prev => ({
              ...prev,
              [selectedState]: payload
          }));
          
          alert(`Shipping rates for ${selectedState} updated!`);
      } catch (err) {
          alert("Failed to save rates.");
      } finally {
          setSaving(false);
      }
  };

  if (loading) return <div className="p-10 text-center">Loading Shipping Data...</div>;

  return (
    <div className="pb-10">
      <h1 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <FaMapMarkedAlt className="text-primary" /> Shipping Rate Management
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* LEFT: CONFIGURATION FORM */}
          <div className="lg:col-span-2">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <h2 className="text-lg font-bold text-gray-800 mb-4">Configure Rates</h2>
                  
                  <form onSubmit={handleSave}>
                      <div className="mb-4">
                          <label className="block text-sm font-bold text-gray-700 mb-1">Select State</label>
                          <select 
                              value={selectedState} 
                              onChange={handleStateSelect} 
                              className="w-full p-3 border rounded bg-white focus:ring-2 focus:ring-primary outline-none"
                          >
                              <option value="">-- Choose State --</option>
                              {Object.keys(groupedLgas).sort().map(state => (
                                  <option key={state} value={state}>{state}</option>
                              ))}
                          </select>
                      </div>

                      {selectedState && (
                          <div className="animate-fadeInUp">
                              <div className="mb-6 bg-blue-50 p-4 rounded border border-blue-100">
                                  <label className="block text-sm font-bold text-blue-800 mb-1">Default Price for {selectedState} (NGN)</label>
                                  <input 
                                      type="number" 
                                      value={defaultPrice} 
                                      onChange={(e) => setDefaultPrice(e.target.value)} 
                                      className="w-full p-3 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                      placeholder="e.g. 3000"
                                      required
                                  />
                                  <p className="text-xs text-blue-600 mt-1">This price applies to all LGAs unless overridden below.</p>
                              </div>

                              <h3 className="font-bold text-gray-700 mb-3">LGA Specific Overrides (Optional)</h3>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto pr-2 border p-2 rounded bg-gray-50">
                                  {groupedLgas[selectedState].map(lga => (
                                      <div key={lga} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200">
                                          <span className="text-sm text-gray-700 font-medium">{lga}</span>
                                          <input 
                                              type="number" 
                                              value={lgaOverrides[lga] || ''} 
                                              onChange={(e) => handleOverrideChange(lga, e.target.value)} 
                                              className="w-24 p-1 border rounded text-right text-sm focus:border-primary outline-none"
                                              placeholder="Default"
                                          />
                                      </div>
                                  ))}
                              </div>

                              <div className="mt-6 flex justify-end">
                                  <button 
                                      type="submit" 
                                      disabled={saving}
                                      className="bg-primary hover:bg-primary-dark text-white font-bold py-3 px-8 rounded shadow-md flex items-center gap-2"
                                  >
                                      {saving ? <FaSpinner className="animate-spin" /> : <FaSave />} Save Rates
                                  </button>
                              </div>
                          </div>
                      )}
                  </form>
              </div>
          </div>

          {/* RIGHT: CURRENT RATES SUMMARY */}
          <div className="lg:col-span-1">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <h2 className="text-lg font-bold text-gray-800 mb-4">Configured States</h2>
                  
                  {Object.keys(configuredRates).length === 0 ? (
                      <p className="text-gray-500 text-sm">No custom rates configured yet.</p>
                  ) : (
                      <div className="space-y-2">
                          {Object.keys(configuredRates).sort().map(state => {
                              const rate = configuredRates[state];
                              const overrideCount = Object.keys(rate.lga_rates || {}).length;
                              const isExpanded = expandedState === state;

                              return (
                                  <div key={state} className="border border-gray-200 rounded">
                                      <button 
                                          onClick={() => setExpandedState(isExpanded ? null : state)}
                                          className="w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                                      >
                                          <div>
                                              <span className="font-bold text-gray-800 block">{state}</span>
                                              <span className="text-xs text-gray-500">Default: {helpers.formatCurrency(rate.default_price, 'NGN')}</span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                              {overrideCount > 0 && (
                                                  <span className="text-[10px] bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                                                      {overrideCount} overrides
                                                  </span>
                                              )}
                                              {isExpanded ? <FaChevronDown className="text-gray-400" /> : <FaChevronRight className="text-gray-400" />}
                                          </div>
                                      </button>
                                      
                                      {isExpanded && overrideCount > 0 && (
                                          <div className="p-3 bg-white border-t border-gray-200 text-sm space-y-1">
                                              {Object.entries(rate.lga_rates).map(([lga, price]) => (
                                                  <div key={lga} className="flex justify-between">
                                                      <span className="text-gray-600">{lga}</span>
                                                      <span className="font-bold text-gray-800">{helpers.formatCurrency(price, 'NGN')}</span>
                                                  </div>
                                              ))}
                                          </div>
                                      )}
                                      
                                      {isExpanded && (
                                          <div className="p-2 border-t border-gray-200 text-right">
                                              <button 
                                                  onClick={() => {
                                                      setSelectedState(state);
                                                      setDefaultPrice(rate.default_price);
                                                      setLgaOverrides(rate.lga_rates || {});
                                                      window.scrollTo({ top: 0, behavior: 'smooth' });
                                                  }}
                                                  className="text-xs text-blue-600 hover:underline flex items-center justify-end gap-1 w-full"
                                              >
                                                  <FaEdit /> Edit Configuration
                                              </button>
                                          </div>
                                      )}
                                  </div>
                              );
                          })}
                      </div>
                  )}
              </div>
          </div>

      </div>
    </div>
  );
}

export default AdminShipping;